import { useTranslation } from 'react-i18next'
import { Plus, Trash } from 'lucide-react'
import { Button, Input, Select } from '@ludus/ui'
import {
  RULE_OPERATORS,
  noOpenCardPreset,
  takesBoolean,
  takesList,
  type RuleOperator,
  type RuleRow,
} from '@/features/flows/types.ts'
import type { PipelineChoice } from './useDrawerOptions.ts'

/**
 * A rule as a list of rows, over the closed operator set (D5).
 *
 * **The editable shape is flatter than the stored one, deliberately.** A stored
 * rule is a tree — `{ _and: [ … ] }`, and `{ $sale: { openCardsIn: { … } } }`
 * for the sale scope — but v1's rules are all one level of `_and`/`_or` over
 * `path op value`, so the builder edits that and refuses to invent a UI for
 * nesting nobody can yet produce. A rule the builder cannot express is left to
 * the raw JSON box rather than silently flattened, because rewriting a stored
 * rule on open would make *looking at* a condition a change to it.
 *
 * `_null` / `_nnull` take a boolean and therefore hide the value input; `_in` /
 * `_nin` take a list, entered comma-separated. That mapping lives in core
 * (`RULE_OPERATOR_COVERAGE`) and is mirrored here only as which control to draw.
 */

interface RuleBuilderProps {
  rows: RuleRow[]
  onChange: (rows: RuleRow[]) => void
  /** Offered for the preset's target board. */
  pipelines: PipelineChoice[]
  paths: string[]
}

export function RuleBuilder({ rows, onChange, pipelines, paths }: RuleBuilderProps) {
  const { t } = useTranslation()

  const set = (i: number, patch: Partial<RuleRow>): void =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 && (
        <p className="text-xs italic text-muted">
          {t('pages.flows.drawer.rule.empty', {
            defaultValue: 'A condition needs something to test.',
          })}
        </p>
      )}

      {rows.map((row, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-lg border border-border p-2">
          <div className="flex items-center gap-1.5">
            <Input
              value={row.path}
              onChange={(e) => set(i, { path: e.target.value })}
              placeholder={t('pages.flows.drawer.rule.path', { defaultValue: 'field' })}
              list={`rule-paths-${i}`}
              className="flex-1 text-xs"
            />
            <datalist id={`rule-paths-${i}`}>
              {paths.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <Select
              value={row.operator}
              onValueChange={(operator) => set(i, { operator: operator as RuleOperator })}
              options={RULE_OPERATORS.map((op) => ({
                value: op,
                label: t(`pages.flows.drawer.rule.op.${op}`, { defaultValue: op }),
              }))}
              triggerClassName="w-32 text-xs"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              aria-label={t('pages.flows.drawer.rule.remove', { defaultValue: 'Remove condition' })}
            >
              <Trash className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>

          {!takesBoolean(row.operator) && (
            <Input
              value={row.value}
              onChange={(e) => set(i, { value: e.target.value })}
              placeholder={
                takesList(row.operator)
                  ? t('pages.flows.drawer.rule.list', { defaultValue: 'a, b, c' })
                  : t('pages.flows.drawer.rule.value', { defaultValue: 'value' })
              }
              className="text-xs"
            />
          )}
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => onChange([...rows, { path: '', operator: '_eq', value: '' }])}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('pages.flows.drawer.rule.add', { defaultValue: 'Add condition' })}
        </Button>

        {pipelines.length > 0 && (
          <Select
            value=""
            onValueChange={(pipelineId) =>
              pipelineId !== '' && onChange([...rows, noOpenCardPreset(pipelineId)])
            }
            placeholder={t('pages.flows.drawer.rule.preset', {
              defaultValue: 'No open card in…',
            })}
            options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
            triggerClassName="text-xs"
          />
        )}
      </div>

      <p className="text-[11px] leading-snug text-muted">
        {t('pages.flows.drawer.rule.missingIsFalse', {
          defaultValue: 'A missing field counts as false.',
        })}
      </p>
    </div>
  )
}
